import React from 'react'

import {
  AlignItems,
  Body2,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  FlexDirection,
  Heading6,
  Icon,
  IconVariant,
  JustifyContent,
  Space,
} from '@gousto-internal/citrus-react'
import type { Recipe } from '@library/menu-service'

import { FlexPortionPicker } from './FlexPortionPicker'

export const DEFAULT_PORTIONS = [
  { numPortions: 1, isAvailable: false },
  { numPortions: 2, isAvailable: false },
  { numPortions: 3, isAvailable: false },
  { numPortions: 4, isAvailable: false },
  { numPortions: 5, isAvailable: false },
]

type FlexPortionEditorProps = {
  recipeCount: number
  numPortions: number
  portions: Recipe['portions']
  onUpdatePortions: (numPortions: number) => void
  onSelectPortions?: (numPortions: number) => void
  onRemoveRecipe: () => void
  onClose: () => void
}

export const FlexPortionEditor = ({
  recipeCount,
  numPortions,
  portions,
  onUpdatePortions,
  onSelectPortions,
  onRemoveRecipe,
  onClose,
}: FlexPortionEditorProps) => {
  const [selectedNumPortions, setSelectedNumPortions] = React.useState(numPortions)
  const portionOptions = portions ?? DEFAULT_PORTIONS

  React.useEffect(() => {
    setSelectedNumPortions(numPortions)
  }, [numPortions])

  const selectedPortion = portionOptions.find(
    (portion) => portion.numPortions === selectedNumPortions,
  )
  const updateDisabled =
    selectedNumPortions === numPortions || selectedPortion?.isAvailable !== true
  const showRecipeCardInfo = selectedNumPortions === 1 || selectedNumPortions === 5
  const handleSelectPortions = (updatedNumPortions: number) => {
    onSelectPortions?.(updatedNumPortions)
    setSelectedNumPortions(updatedNumPortions)
  }

  return (
    <Box bg={Color.White} data-testid="flex-portion-editor" width="100%">
      <Box
        alignItems={AlignItems.Center}
        borderBottomWidth={0.5}
        borderColor={Color.ColdGrey_100}
        borderStyle={BorderStyle.Solid}
        display={Display.Flex}
        justifyContent={JustifyContent.SpaceBetween}
        paddingH={4}
        paddingV={1}
      >
        <Heading6>{`${recipeCount} ${recipeCount === 1 ? 'recipe' : 'recipes'}`}</Heading6>
        <Button
          aria-label="Close portion editor"
          colorVariant={ButtonColorVariant.Tertiary}
          icon="close"
          onClick={onClose}
          variant={ButtonVariant.Icon}
        />
      </Box>
      <Box display={Display.Flex} flexDirection={FlexDirection.Column} paddingH={4} paddingV={4}>
        <Box
          aria-describedby="flex-portions-description"
          aria-label="Select number of portions"
          role="group"
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={4}
          paddingBottom={4}
        >
          <Heading6>How many portions?</Heading6>
          <FlexPortionPicker
            onChange={handleSelectPortions}
            portions={portionOptions}
            selectedNumPortions={selectedNumPortions}
          />
        </Box>
        <Box
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={3}
          paddingH={4}
          paddingBottom={4}
        >
          {showRecipeCardInfo && (
            <Body2 aria-live="polite" size={2}>
              Heads up: When you cook a recipe for {selectedNumPortions}, you&apos;ll need to use
              your Gousto app for cooking instructions.
            </Body2>
          )}
          <Body2 id="flex-portions-description" size={2}>
            We calculate portion prices at checkout based on the total recipes and portions in your
            box.
          </Body2>
        </Box>
        <Box display={Display.Flex} flexDirection={FlexDirection.Column}>
          <Button
            disabled={updateDisabled}
            onClick={() => onUpdatePortions(selectedNumPortions)}
            width="100%"
          >
            Update portions
          </Button>
          <Space direction="vertical" size={2} />
          <Button colorVariant={ButtonColorVariant.Tertiary} onClick={onRemoveRecipe} width="100%">
            <Icon name="delete" variant={IconVariant.Actionable} />
            <Space direction="horizontal" size={1} />
            Remove recipe
          </Button>
        </Box>
      </Box>
    </Box>
  )
}
